// src/screens/EventsBookingsList.jsx — GET /bookings, GET /bookings/:id, DELETE /bookings/:id
import "bootstrap-icons/font/bootstrap-icons.css";
import { useCallback, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import "../css/EventsBookings.css";
import useIdleLogout from "../hooks/useIdleLogout";

const LOGS_API = (process.env.REACT_APP_ADMIN_LOGS_API_URL || "").trim();
function postLog(description) {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const name = user?.user_name || user?.name || "Admin";
    const token = localStorage.getItem("access_token") || "";
    fetch(LOGS_API, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ description, user: name, admin_name: name, timestamp: new Date().toISOString() }),
    }).catch(() => {});
  } catch { }
}

const EVENTS_BASE = (process.env.REACT_APP_EVENTS_API_URL || "").replace(/\/+$/, "");
const PAGE_LIMIT  = 20;
const PAYMENT_METHODS = ["WALLET", "CARD", "UPI"];

function getBearerToken() {
  const t = localStorage.getItem("access_token") || localStorage.getItem("token") || sessionStorage.getItem("access_token");
  if (t) return String(t).trim();
  try { const u=JSON.parse(localStorage.getItem("user")||"{}"); return String(u?.access_token||u?.token||"").trim(); } catch { return ""; }
}
function getAuthHeaders() {
  const h = { Accept:"application/json" };
  const t = getBearerToken();
  if (t) h.Authorization = `Bearer ${t}`;
  return h;
}

const fmt = (n) => `BTN. ${Number(n||0).toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2})}`;
const fmtDate = (ts) => ts ? new Date(ts).toLocaleString("en-US",{timeZone:"Asia/Thimphu",month:"short",day:"numeric",year:"numeric",hour:"numeric",minute:"2-digit",hour12:true}) : "—";

const STATUS_COLORS = { confirmed:"success", pending:"warning", cancelled:"danger", canceled:"danger" };

export default function EventsBookingsList() {
  useIdleLogout();

  const [bookings, setBookings]   = useState([]);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(1);
  const [loading, setLoading]     = useState(false);
  const [err, setErr]             = useState("");
  const [search, setSearch]       = useState("");
  const [fromDate, setFromDate]   = useState("");
  const [toDate, setToDate]       = useState("");
  const [payMethod, setPayMethod] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  const fetchBookings = useCallback(async (q, pm, from, to, pg) => {
    if (!EVENTS_BASE) { setErr("Events API URL not configured."); return; }
    try {
      setLoading(true); setErr("");
      const url = new URL(`${EVENTS_BASE}/bookings`);
      if (q)    url.searchParams.set("q", q);
      if (pm)   url.searchParams.set("payment_method", pm);
      if (from) url.searchParams.set("from_date", from);
      if (to)   url.searchParams.set("to_date", to);
      url.searchParams.set("page", String(pg));
      url.searchParams.set("limit", String(PAGE_LIMIT));
      const res  = await fetch(url.toString(), { headers: getAuthHeaders(), cache:"no-store" });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || `HTTP ${res.status}`);
      const rows = Array.isArray(json?.data) ? json.data : Array.isArray(json) ? json : [];
      setBookings(rows);
      setTotal(json?.total ?? rows.length);
    } catch(e) { setErr(e.message||"Failed to load bookings."); setBookings([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchBookings("","",fromDate,toDate,1); }, []); // eslint-disable-line

  const handleApply = () => { setPage(1); fetchBookings(search,payMethod,fromDate,toDate,1); };
  const handlePage  = (p) => { setPage(p); fetchBookings(search,payMethod,fromDate,toDate,p); };

  /* metrics */
  const confirmed = useMemo(()=>bookings.filter(b=>b.status==="confirmed").length,[bookings]);
  const revenue   = useMemo(()=>bookings.filter(b=>b.status==="confirmed").reduce((s,b)=>s+Number(b.total_amount||0),0),[bookings]);

  /* VIEW DETAIL */
  const handleView = async (b) => {
    try {
      const res  = await fetch(`${EVENTS_BASE}/bookings/${b.id}`, { headers: getAuthHeaders() });
      const json = await res.json().catch(() => ({}));
      const d    = json?.data || b;
      Swal.fire({
        title: `Booking ${d.id}`, width:"680px", confirmButtonText:"Close", showCancelButton:false,
        html:`<div class="text-start" style="font-size:13px">
          <div class="row g-2">
            <div class="col-6"><strong>Ticket Code:</strong><br/><code>${d.ticket_code||"—"}</code></div>
            <div class="col-6"><strong>Status:</strong><br/><span class="badge bg-${STATUS_COLORS[d.status]||"secondary"}">${d.status||"—"}</span></div>
            <div class="col-6"><strong>Event:</strong><br/>${d.event_title||"—"}</div>
            <div class="col-6"><strong>Venue:</strong><br/>${d.venue_name||"—"}</div>
            <div class="col-6"><strong>Tier:</strong><br/>${d.tier_name||"—"}</div>
            <div class="col-6"><strong>Quantity:</strong><br/>${d.quantity||"—"}</div>
            <div class="col-6"><strong>Total Amount:</strong><br/><strong>${fmt(d.total_amount)}</strong></div>
            <div class="col-6"><strong>Payment Method:</strong><br/>${d.payment_method||"—"}</div>
            <div class="col-6"><strong>Customer:</strong><br/>${d.user_name||"—"}</div>
            <div class="col-6"><strong>Email:</strong><br/>${d.user_email||"—"}</div>
            <div class="col-6"><strong>Phone:</strong><br/>${d.user_phone||"—"}</div>
            <div class="col-6"><strong>Booked At:</strong><br/>${fmtDate(d.created_at)}</div>
            ${d.attendee_names?.length ? `<div class="col-12"><strong>Attendees:</strong><br/>${d.attendee_names.join(", ")}</div>` : ""}
            ${d.wallet_journal_code ? `<div class="col-12"><strong>Journal Code:</strong><br/><code>${d.wallet_journal_code}</code></div>` : ""}
            ${d.event_seats?.length ? `<div class="col-12"><strong>Seats:</strong><br/>${d.event_seats.map(s=>`${s.row}${s.number} (${s.section})`).join(", ")}</div>` : ""}
          </div>
        </div>`,
      });
    } catch(e) { Swal.fire({icon:"error",title:"Failed",text:e.message}); }
  };

  /* DELETE */
  const handleDelete = async (b) => {
    const { isConfirmed } = await Swal.fire({
      icon:"warning", title:`Delete booking ${b.id}?`,
      text:"This permanently removes the booking and its seat records.",
      showCancelButton:true, confirmButtonColor:"#dc3545", confirmButtonText:"Yes, Delete",
    });
    if (!isConfirmed) return;
    try {
      setDeletingId(b.id);
      const res = await fetch(`${EVENTS_BASE}/bookings/${b.id}`, { method:"DELETE", headers:getAuthHeaders() });
      if (!res.ok) { const j=await res.json().catch(()=>({})); throw new Error(j?.message||`HTTP ${res.status}`); }
      postLog(`Deleted booking ${b.id} (event: ${b.event_title || "—"})`);
      Swal.fire({icon:"success",title:"Deleted!",timer:1200,showConfirmButton:false});
      fetchBookings(search,payMethod,fromDate,toDate,page);
    } catch(e) { Swal.fire({icon:"error",title:"Failed",text:e.message}); }
    finally { setDeletingId(null); }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_LIMIT));

  return (
    <>
      {err && <div className="alert alert-danger" role="alert">{err}</div>}

      {/* METRICS */}
      <div className="row row-cols-1 row-cols-md-2 row-cols-lg-4 g-3 mb-4 events-metrics">
        {[
          {label:"Total Bookings",    value: loading ? "…" : total.toLocaleString()},
          {label:"Confirmed",         value: loading ? "…" : confirmed.toLocaleString()},
          {label:"Confirmed Revenue", value: loading ? "…" : fmt(revenue)},
          {label:"Showing",           value: loading ? "…" : `${bookings.length} of ${total}`},
        ].map((m,i)=>(
          <div className="col" key={i}>
            <div className="card metrics-card h-100">
              <div className="card-body">
                <h6 className="text-secondary mb-1">{m.label}</h6>
                <div className="fs-2 fw-bold">{m.value}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* TOOLBAR */}
      <div className="card mb-3 search-filter-card">
        <div className="card-body searchbar-row-inline">
          <span className="search-icon"><i className="bi bi-search" /></span>
          <div className="search-wrap">
            <input type="text" className="form-control search-pill" placeholder="Search by booking ID, ticket code…"
              value={search} onChange={e=>setSearch(e.target.value)} onKeyDown={e=>e.key==="Enter"&&handleApply()} />
          </div>
          <select className="select-pill" value={payMethod} onChange={e=>setPayMethod(e.target.value)}>
            <option value="">All Payments</option>
            {PAYMENT_METHODS.map(p=><option key={p} value={p}>{p}</option>)}
          </select>
          <div className="date-range">
            <input type="date" className="date-pill" value={fromDate} onChange={e=>setFromDate(e.target.value)} />
            <span className="range-sep">to</span>
            <input type="date" className="date-pill" value={toDate} onChange={e=>setToDate(e.target.value)} />
          </div>
          <div className="exports ms-auto">
            <button className="btn btn-primary" onClick={handleApply}>Apply</button>
          </div>
        </div>
      </div>

      {/* TABLE */}
      <div className="bookings-table-card card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Bookings</h5>
          <small>{total} total</small>
        </div>
        <div className="table-responsive">
          <table className="table table-hover align-middle table-pro">
            <colgroup>
              <col style={{width:"50px"}}/><col style={{width:"140px"}}/><col style={{width:"110px"}}/>
              <col style={{width:"220px"}}/><col style={{width:"90px"}}/><col style={{width:"50px"}}/>
              <col style={{width:"110px"}}/><col style={{width:"80px"}}/><col style={{width:"90px"}}/>
              <col style={{width:"140px"}}/><col style={{width:"90px"}}/>
            </colgroup>
            <thead>
              <tr>
                <th>SI.NO</th><th>Booking ID</th><th>Ticket Code</th><th>Event</th>
                <th>Tier</th><th>Qty</th><th>Amount</th><th>Payment</th>
                <th>Status</th><th>Customer</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={11} className="py-4 text-center text-muted"><span className="spinner-border spinner-border-sm me-2"/>Loading…</td></tr>}
              {!loading && bookings.length===0 && <tr><td colSpan={11} className="text-center py-4 text-muted">No bookings found.</td></tr>}
              {!loading && bookings.map((b,idx)=>(
                <tr key={b.id}>
                  <td>{(page-1)*PAGE_LIMIT+idx+1}</td>
                  <td className="mono" style={{fontSize:12}}>{b.id}</td>
                  <td className="mono" style={{fontSize:11}}>{b.ticket_code||"—"}</td>
                  <td>
                    <div style={{fontSize:13,fontWeight:600}}>{b.event_title||"—"}</div>
                    <div className="text-muted" style={{fontSize:11}}>{b.venue_name||""}</div>
                  </td>
                  <td style={{fontSize:13}}>{b.tier_name||"—"}</td>
                  <td className="mono">{b.quantity||0}</td>
                  <td className="mono nowrap amount-cell">{fmt(b.total_amount)}</td>
                  <td style={{fontSize:13}}>{b.payment_method||"—"}</td>
                  <td>
                    <span className={`badge bg-${STATUS_COLORS[b.status]||"secondary"}`}>{b.status||"—"}</span>
                  </td>
                  <td>
                    <div style={{fontSize:13}}>{b.user_name||"—"}</div>
                    <div className="text-muted" style={{fontSize:11}}>{b.user_phone||""}</div>
                  </td>
                  <td>
                    <div className="ev-actions">
                      <button className="ev-btn ev-btn-edit" title="View Detail" onClick={()=>handleView(b)}>
                        <i className="bi bi-eye"/>
                      </button>
                      <button className="ev-btn ev-btn-delete" title="Delete" disabled={deletingId===b.id} onClick={()=>handleDelete(b)}>
                        {deletingId===b.id ? <span className="spinner-border spinner-border-sm"/> : <i className="bi bi-trash"/>}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {totalPages>1 && (
          <div className="card-footer d-flex justify-content-between align-items-center py-2 px-3">
            <small className="text-muted">Page {page} of {totalPages} · {total} bookings</small>
            <div className="d-flex gap-1">
              <button className="btn btn-sm btn-outline-secondary" disabled={page<=1} onClick={()=>handlePage(page-1)}>&lsaquo; Prev</button>
              {Array.from({length:Math.min(5,totalPages)},(_,i)=>{
                const start=Math.max(1,Math.min(page-2,totalPages-4)); const p=start+i;
                return p<=totalPages ? <button key={p} className={`btn btn-sm ${p===page?"btn-primary":"btn-outline-secondary"}`} onClick={()=>handlePage(p)}>{p}</button> : null;
              })}
              <button className="btn btn-sm btn-outline-secondary" disabled={page>=totalPages} onClick={()=>handlePage(page+1)}>Next &rsaquo;</button>
            </div>
          </div>
        )}
      </div>

      <footer className="dashboard-footer mt-auto">
        <small>© {new Date().getFullYear()} <strong>སྟབས་བདེ། </strong> Admin • All rights reserved</small>
      </footer>
    </>
  );
}
